import { ChangeDetectionStrategy, Component, computed, signal } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { AviaTraceIdComponent } from '@pages/avia-page/components/avia-trace-id/avia-trace-id.component';
import { OnDynamicModal } from '@shared/modules/dynamic-modal/dynamic-modal.types';
import { PriceFormatPipe } from '@shared/pipes/price-format.pipe';
import {
  EXT_NOT_AVAILABLE,
  EXT_PRICE_CHANGED,
  extensionCodeText,
  extensionKindLabel,
  extensionNight
} from './hotel-extensions.utils';

/** accept the new price / go on without the extension / stay (nothing booked). */
export type HotelExtensionChangeChoice = 'accept' | 'without' | 'cancel';

export interface HotelExtensionChangeData {
  /** EXT_PRICE_CHANGED or EXT_NOT_AVAILABLE */
  code: string;
  kind: number;
  hotelName?: string | null;
  nightFrom?: string | null;
  nightTo?: string | null;
  oldSell?: number | null;
  newSell?: number | null;
  currency?: string | null;
  /** at checkout the booking can go on without it; after booking there is nothing to go on with */
  canGoWithout: boolean;
  activityId?: string | null;
}

/**
 * ECI/LCO V1 (spec section 4, Confirm): the extra night's price moved (EXT_PRICE_CHANGED) - nothing was booked; Accept the new price books again with it;
 * or the night is gone (EXT_NOT_AVAILABLE) - the booking goes on without it only when the agent says so. The same look as the flight dialog.
 */
@Component({
  selector: 'app-hotel-extension-price-changed-dialog',
  templateUrl: './hotel-extension-price-changed-dialog.component.html',
  styleUrl: '../../../pages/avia-page/components/avia-price-changed-dialog/avia-price-changed-dialog.component.scss',
  imports: [TranslatePipe, PriceFormatPipe, AviaTraceIdComponent],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HotelExtensionPriceChangedDialogComponent implements OnDynamicModal<
  HotelExtensionChangeData,
  HotelExtensionChangeChoice
> {
  readonly data = signal<HotelExtensionChangeData | null>(null);
  readonly titleId = 'hotel-extension-changed-title';
  readonly priceChanged = computed(() => this.data()?.code === EXT_PRICE_CHANGED);
  readonly gone = computed(() => this.data()?.code === EXT_NOT_AVAILABLE);
  readonly kindLabel = computed(() => extensionKindLabel(this.data()?.kind ?? 1));
  readonly codeText = extensionCodeText;
  readonly night = computed(() => extensionNight(this.data()?.nightFrom, this.data()?.nightTo));

  onDynamicModalClose?: (result?: HotelExtensionChangeChoice) => void;

  onDynamicModal(data: HotelExtensionChangeData): void {
    this.data.set(data);
  }

  choose(choice: HotelExtensionChangeChoice): void {
    this.onDynamicModalClose?.(choice);
  }
}
